/** @jsxImportSource @emotion/react */
import { Container, Grid, Stack, Text, useTheme } from "basis";
import { Separator } from "components/Separator/Separator";
import { AccountInfo } from "library/hooks/types";
import { formatAmount } from "library/moneyUtils";
import { getMinMonthlyPayment } from "../utils";

interface Props {
  accountInfo: AccountInfo;
  newCreditLimit: string;
  errors?: { newCreditLimit: string };
  message?: {
    newLimit: string;
    newMonthlyPayment: string;
    oldLimit: string;
    oldMonthlyPayment: string;
  };
}

export const CreditLimitDetails = ({
  accountInfo,
  newCreditLimit,
  errors,
  message = {
    newLimit: "Requested credit limit",
    newMonthlyPayment: "Minimum monthly payment",
    oldLimit: "Current credit limit",
    oldMonthlyPayment: "Minimum monthly payment",
  },
}: Props) => {
  const theme = useTheme();
  const isNewCreditLimitValid =
    /^\d+$/.test(newCreditLimit) && errors?.newCreditLimit === undefined;

  return (
    <Container hasBorder padding="4">
      <Grid cols-md="1fr 1px 1fr">
        <Stack gap="4" margin="0 0 4 0" margin-md="0 4 0 0">
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Text>{message.newLimit}</Text>
            <Text testId="new-credit-limit">
              <strong>
                {isNewCreditLimitValid ? formatAmount(newCreditLimit) : "–"}
              </strong>
            </Text>
          </div>
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Text>{message.newMonthlyPayment}</Text>
            <Text testId="minimum-monthly-payment">
              <strong>
                {isNewCreditLimitValid
                  ? formatAmount(
                      getMinMonthlyPayment({
                        creditLimit: newCreditLimit,
                        minMonthlyPaymentPercentage:
                          accountInfo.cardMmpPercentage,
                        minMonthlyPaymentThreshold:
                          accountInfo.cardMmpThreshold,
                      }),
                    )
                  : "–"}
              </strong>
            </Text>
          </div>
        </Stack>
        <div>
          <div
            css={{
              [theme.minMediaQueries.md]: {
                display: "none",
              },
            }}
          >
            <Separator />
          </div>
          <div
            css={{
              display: "none",
              [theme.minMediaQueries.md]: {
                display: "initial",
              },
            }}
          >
            <Separator vertical />
          </div>
        </div>
        <Stack gap="4" margin="4 0 0 0" margin-md="0 0 0 4">
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Text>{message.oldLimit}</Text>
            <Text testId="old-credit-limit">
              {formatAmount(accountInfo.creditLimit)}
            </Text>
          </div>
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Text>{message.oldMonthlyPayment}</Text>
            <Text testId="old-minimum-monthly-payment">
              {formatAmount(
                getMinMonthlyPayment({
                  creditLimit: accountInfo.creditLimit,
                  minMonthlyPaymentPercentage: accountInfo.cardMmpPercentage,
                  minMonthlyPaymentThreshold: accountInfo.cardMmpThreshold,
                }),
              )}
            </Text>
          </div>
        </Stack>
      </Grid>
    </Container>
  );
};
